// Service catalog — data-driven service landing pages.
// Each entry powers a dedicated, keyword-targeted page at /services/{slug}.

export const SERVICES = [
  {
    slug: 'dry-van-dispatch',
    name: 'Dry Van Dispatch Services',
    shortName: 'Dry Van Dispatch',
    icon: 'Container',
    category: 'equipment',
    seo: {
      title: 'Dry Van Dispatch Services | Flow Fleet Solutions',
      description:
        'Professional dry van dispatch services for owner operators and fleets. We book premium dry van loads, negotiate top rates, and handle paperwork so you stay loaded and profitable.',
      keywords: ['dry van dispatch', 'dry van dispatch service', 'owner operator dispatch', 'dry van loads', 'truck dispatch services'],
    },
    hero: {
      headline: 'Dry Van Dispatch That Keeps You Loaded',
      sub: 'Consistent freight, strong rates, and zero downtime. We find the best-paying dry van loads while you focus on driving.',
    },
    intro:
      'Dry van is the backbone of freight, and competition for the best loads is fierce. Our dispatchers work the load boards and our broker network around the clock to keep your dry van moving on high-paying lanes with reliable, credit-checked brokers.',
    benefits: [
      { title: 'Premium Load Sourcing', desc: 'Access to top-paying dry van freight across regional and OTR lanes.' },
      { title: 'Aggressive Rate Negotiation', desc: 'We push for every dollar so your linehaul stays well above market average.' },
      { title: 'Backhaul Planning', desc: 'Smart lane planning that minimizes deadhead and maximizes loaded miles.' },
      { title: 'Full Paperwork Handling', desc: 'Rate cons, broker packets, and check calls managed end to end.' },
    ],
    equipment: ['53\' Dry Van', '48\' Dry Van', 'Pup Trailers', 'Multi-stop Van Freight'],
    industries: ['Retail & E-commerce', 'Consumer Goods', 'Paper & Packaging', 'Building Materials'],
    faqs: [
      { q: 'How quickly can you start dispatching my dry van?', a: 'Most carriers are set up and receiving load options within 24 hours of completing onboarding.' },
      { q: 'What percentage or fee do you charge for dry van dispatch?', a: 'We work on a simple flat dispatch fee per load with no long-term contracts. Contact us for current rates.' },
      { q: 'Do you only book OTR or also regional dry van loads?', a: 'Both. Tell us your preferred lanes and home-time goals and we build your freight around them.' },
      { q: 'Do you check broker credit before booking?', a: 'Always. We run broker credit checks on every load to protect your payment.' },
    ],
    related: ['reefer-dispatch', 'power-only-dispatch', 'rate-negotiation'],
  },
  {
    slug: 'reefer-dispatch',
    name: 'Reefer Dispatch Services',
    shortName: 'Reefer Dispatch',
    icon: 'Snowflake',
    category: 'equipment',
    seo: {
      title: 'Reefer Dispatch Services | Refrigerated Freight Dispatcher',
      description:
        'Expert reefer dispatch services for refrigerated carriers. Maximize revenue on temperature-controlled freight with premium reefer loads, strong rates, and dedicated dispatch support.',
      keywords: ['reefer dispatch', 'reefer dispatcher', 'refrigerated freight dispatch', 'reefer loads', 'temperature controlled dispatch'],
    },
    hero: {
      headline: 'Reefer Dispatch for Maximum Revenue Per Mile',
      sub: 'Temperature-controlled freight pays more when you have the right dispatcher. We keep your reefer full on the highest-paying produce and protein lanes.',
    },
    intro:
      'Reefer freight rewards carriers who can move fast and protect cold-chain integrity. Our reefer dispatchers understand temperature requirements, multi-stop produce runs, and seasonal demand so you stay loaded on premium refrigerated lanes year-round.',
    benefits: [
      { title: 'High-Paying Reefer Lanes', desc: 'Priority access to produce, meat, dairy, and frozen freight that pays a premium.' },
      { title: 'Cold-Chain Aware Booking', desc: 'We confirm temp requirements and detention terms before you commit.' },
      { title: 'Seasonal Lane Strategy', desc: 'We plan around produce season and regional demand to keep rates high.' },
      { title: 'Detention & Lumper Support', desc: 'We fight for detention pay and manage lumper reimbursements for you.' },
    ],
    equipment: ['53\' Reefer', 'Multi-temp Reefer', 'Produce Reefer', 'Frozen Freight'],
    industries: ['Produce & Agriculture', 'Meat & Poultry', 'Dairy', 'Frozen Foods & Pharma'],
    faqs: [
      { q: 'Do you understand reefer temperature and detention requirements?', a: 'Yes. We confirm set temps, continuous vs. cycle, and detention terms on every reefer load before booking.' },
      { q: 'Can you keep me loaded during slow produce season?', a: 'Absolutely. We diversify into frozen, protein, and dry-capable reefer freight when produce slows.' },
      { q: 'How do you handle lumper fees?', a: 'We negotiate lumper reimbursement upfront and track it through to settlement.' },
      { q: 'What rates can I expect on reefer freight?', a: 'Reefer typically pays above dry van. We negotiate aggressively to keep you at the top of market.' },
    ],
    related: ['dry-van-dispatch', 'rate-negotiation', 'broker-credit-checks'],
  },
  {
    slug: 'flatbed-dispatch',
    name: 'Flatbed Dispatch Services',
    shortName: 'Flatbed Dispatch',
    icon: 'Layers',
    category: 'equipment',
    seo: {
      title: 'Flatbed Dispatch Service | Step Deck & Open Deck Dispatcher',
      description:
        'Specialized flatbed dispatch service for open-deck carriers. We book high-paying flatbed, step deck, and heavy-haul loads, secure permits, and negotiate top rates.',
      keywords: ['flatbed dispatch', 'flatbed dispatch service', 'step deck dispatch', 'open deck dispatcher', 'flatbed loads'],
    },
    hero: {
      headline: 'Flatbed Dispatch for Open-Deck Pros',
      sub: 'From steel and lumber to machinery and oversize, we book strong flatbed and step deck freight and handle the details that matter.',
    },
    intro:
      'Flatbed freight demands experience with securement, tarping, and permits. Our flatbed dispatchers specialize in open-deck loads, matching your equipment to high-value lanes while handling securement requirements and oversize logistics.',
    benefits: [
      { title: 'High-Value Open-Deck Freight', desc: 'Steel, lumber, machinery, and building materials on premium lanes.' },
      { title: 'Tarp & Securement Clarity', desc: 'We confirm tarping, strap, and chain requirements before you book.' },
      { title: 'Permit & Oversize Support', desc: 'Help coordinating permits and routing for oversize and overweight loads.' },
      { title: 'Tarp Pay Negotiation', desc: 'We negotiate accessorial and tarp pay so you are compensated for the extra work.' },
    ],
    equipment: ['48\'/53\' Flatbed', 'Step Deck', 'Conestoga', 'RGN / Lowboy'],
    industries: ['Steel & Metals', 'Lumber & Building Materials', 'Heavy Machinery', 'Construction'],
    faqs: [
      { q: 'Do you book oversize and overweight flatbed loads?', a: 'Yes, and we help coordinate permits and routing for oversize and heavy-haul freight.' },
      { q: 'Will you negotiate tarp pay?', a: 'Always. Tarping is extra work and we make sure it is paid for on every applicable load.' },
      { q: 'Can you dispatch step deck and RGN equipment?', a: 'Yes, we dispatch flatbed, step deck, Conestoga, and RGN/lowboy carriers.' },
      { q: 'How do you handle securement requirements?', a: 'We confirm strap, chain, and securement requirements on the rate con before you commit.' },
    ],
    related: ['power-only-dispatch', 'rate-negotiation', 'carrier-compliance'],
  },
  {
    slug: 'power-only-dispatch',
    name: 'Power Only Dispatch',
    shortName: 'Power Only',
    icon: 'Truck',
    category: 'equipment',
    seo: {
      title: 'Power Only Dispatch Services | Drop & Hook Dispatcher',
      description:
        'Power only dispatch services for carriers running drop-and-hook freight. Keep your tractor loaded with consistent power only loads and dedicated dispatch support.',
      keywords: ['power only dispatch', 'power only loads', 'drop and hook dispatch', 'power only dispatcher', 'tractor only freight'],
    },
    hero: {
      headline: 'Power Only Dispatch, Maximum Uptime',
      sub: 'Run your tractor lean and loaded. We source consistent power only and drop-and-hook freight to keep your wheels turning.',
    },
    intro:
      'Power only lets you maximize revenue without the cost of owning trailers. Our dispatchers connect you to reliable drop-and-hook programs and power only freight so you spend more time loaded and less time waiting.',
    benefits: [
      { title: 'Consistent Power Only Freight', desc: 'Steady access to power only and drop-and-hook programs nationwide.' },
      { title: 'Reduced Detention', desc: 'Drop-and-hook freight means less waiting and more loaded miles.' },
      { title: 'Trailer Pool Access', desc: 'We connect you with shippers and 3PLs offering trailer pools.' },
      { title: 'Quick Turnarounds', desc: 'Fast booking so your tractor is rarely sitting empty.' },
    ],
    equipment: ['Day Cab Tractor', 'Sleeper Tractor', 'Drop & Hook', 'Trailer Pool Freight'],
    industries: ['Retail Distribution', '3PL & Brokerage', 'Manufacturing', 'Intermodal'],
    faqs: [
      { q: 'What is power only dispatch?', a: 'You provide the tractor and we book freight where the shipper or broker supplies the trailer, often drop-and-hook.' },
      { q: 'Do I need my own trailer?', a: 'No. Power only freight uses the customer\'s trailer, which is why it is popular for lowering overhead.' },
      { q: 'Is power only freight consistent?', a: 'With the right dispatcher and broker relationships, yes. We keep a pipeline of power only programs ready.' },
      { q: 'Can I mix power only with other freight?', a: 'Yes. We blend power only with other loads based on your equipment and lane preferences.' },
    ],
    related: ['dry-van-dispatch', 'flatbed-dispatch', 'rate-negotiation'],
  },
  {
    slug: 'hotshot-dispatch',
    name: 'Hotshot Dispatch Services',
    shortName: 'Hotshot Dispatch',
    icon: 'Zap',
    category: 'equipment',
    seo: {
      title: 'Hotshot Dispatch Services | Hotshot Trucking Dispatcher',
      description:
        'Hotshot dispatch services for class 3-5 trucks and gooseneck trailers. We book expedited, time-sensitive hotshot loads and negotiate premium rates for fast freight.',
      keywords: ['hotshot dispatch', 'hotshot dispatch service', 'hotshot trucking dispatcher', 'hotshot loads', 'expedited dispatch'],
    },
    hero: {
      headline: 'Hotshot Dispatch for Fast, High-Paying Freight',
      sub: 'Time-sensitive loads pay more. We keep your hotshot rig booked on expedited freight that values speed.',
    },
    intro:
      'Hotshot trucking thrives on urgency. Our dispatchers find expedited, time-critical loads that match your class 3-5 truck and gooseneck or flatbed trailer, negotiating premium rates for fast, reliable delivery.',
    benefits: [
      { title: 'Expedited Load Access', desc: 'Time-critical freight that pays a premium for speed and reliability.' },
      { title: 'Right-Sized Loads', desc: 'Freight matched to your truck class, deck length, and weight limits.' },
      { title: 'Fast Booking', desc: 'Quick turnarounds so you capitalize on urgent shipper demand.' },
      { title: 'Rate Premiums', desc: 'We push for the higher rates that expedited freight commands.' },
    ],
    equipment: ['Class 3-5 Trucks', 'Gooseneck Trailer', 'Bumper Pull', 'Flatbed Hotshot'],
    industries: ['Oil & Gas', 'Construction', 'Agriculture', 'Equipment & Parts'],
    faqs: [
      { q: 'What size loads work for hotshot?', a: 'We match freight to your deck length and weight capacity, typically partial and expedited LTL-style loads.' },
      { q: 'Is hotshot freight more profitable?', a: 'Expedited and time-sensitive hotshot freight often pays a premium versus standard loads.' },
      { q: 'Do you dispatch gooseneck and bumper-pull setups?', a: 'Yes, we dispatch a range of hotshot configurations including gooseneck and flatbed hotshot.' },
      { q: 'How fast can you find a load?', a: 'Hotshot moves fast and so do we, often securing your next load within hours.' },
    ],
    related: ['box-truck-dispatch', 'flatbed-dispatch', 'mc-authority-assistance'],
  },
  {
    slug: 'box-truck-dispatch',
    name: 'Box Truck Dispatch Services',
    shortName: 'Box Truck Dispatch',
    icon: 'Package',
    category: 'equipment',
    seo: {
      title: 'Box Truck Dispatch Services | Straight Truck Dispatcher',
      description:
        'Box truck dispatch services for straight trucks and cargo vans. We book steady box truck loads, negotiate rates, and handle paperwork for non-CDL and CDL box truck carriers.',
      keywords: ['box truck dispatch', 'box truck dispatch service', 'straight truck dispatcher', 'box truck loads', 'cargo van dispatch'],
    },
    hero: {
      headline: 'Box Truck Dispatch That Keeps You Booked',
      sub: 'From 26-foot straight trucks to cargo vans, we find consistent box truck freight and handle the back office.',
    },
    intro:
      'Box truck and straight truck carriers need a dispatcher who understands non-CDL and CDL freight markets. We keep your box truck loaded with steady regional and expedited freight while we manage rate negotiation and paperwork.',
    benefits: [
      { title: 'Steady Box Truck Freight', desc: 'Consistent loads for 26-foot straight trucks and cargo vans.' },
      { title: 'Expedited Opportunities', desc: 'Access to time-sensitive freight that pays a premium for box trucks.' },
      { title: 'Liftgate & Driver-Assist Clarity', desc: 'We confirm accessorial requirements and pay before booking.' },
      { title: 'Paperwork Handled', desc: 'Rate cons, broker packets, and check calls fully managed.' },
    ],
    equipment: ['26\' Box Truck', 'Straight Truck', 'Cargo Van', 'Sprinter Van'],
    industries: ['Final Mile & E-commerce', 'Furniture & Appliances', 'Medical Supplies', 'Trade Show & Events'],
    faqs: [
      { q: 'Do you dispatch non-CDL box trucks?', a: 'Yes, we dispatch both non-CDL and CDL box truck and straight truck carriers.' },
      { q: 'Can you find expedited box truck loads?', a: 'Yes. Expedited and time-critical freight is a strong fit for box trucks and we prioritize it.' },
      { q: 'Do you handle liftgate and driver-assist freight?', a: 'We confirm liftgate, inside delivery, and driver-assist requirements and negotiate the accessorial pay.' },
      { q: 'Is there enough freight for box trucks?', a: 'Yes. Final-mile and regional demand keeps box truck freight plentiful with the right dispatcher.' },
    ],
    related: ['hotshot-dispatch', 'power-only-dispatch', 'rate-negotiation'],
  },
  {
    slug: 'mc-authority-assistance',
    name: 'MC Authority Assistance',
    shortName: 'MC Authority',
    icon: 'FileBadge',
    category: 'support',
    seo: {
      title: 'MC Authority Assistance | Get Your Operating Authority Fast',
      description:
        'MC authority assistance for new trucking businesses. We guide owner operators through FMCSA registration, MC/DOT numbers, BOC-3, and getting your operating authority active fast.',
      keywords: ['mc authority assistance', 'get mc authority', 'operating authority', 'fmcsa registration', 'new trucking authority'],
    },
    hero: {
      headline: 'Get Your MC Authority the Right Way',
      sub: 'Launching your own authority is a big step. We guide you through FMCSA registration so you start legal, compliant, and ready to roll.',
    },
    intro:
      'Getting your own operating authority unlocks higher earnings but the FMCSA process is full of steps that trip up new carriers. We walk you through MC/DOT registration, BOC-3 filing, UCR, and insurance requirements so your authority activates without costly delays.',
    benefits: [
      { title: 'Guided FMCSA Registration', desc: 'Step-by-step help with MC and DOT number applications.' },
      { title: 'BOC-3 & UCR Filing Support', desc: 'We make sure required filings are completed correctly the first time.' },
      { title: 'Insurance Guidance', desc: 'Help understanding the insurance filings needed to activate authority.' },
      { title: 'Faster Activation', desc: 'Avoid common mistakes that delay your authority for weeks.' },
    ],
    equipment: ['New Owner Operators', 'Fleet Startups', 'Reinstatements', 'Authority Upgrades'],
    industries: ['Owner Operators', 'New Carriers', 'Small Fleets', 'Independent Drivers'],
    faqs: [
      { q: 'How long does it take to get MC authority?', a: 'FMCSA typically requires a 21-day vetting period after filing. We help you prepare so the clock starts immediately.' },
      { q: 'What is a BOC-3 filing?', a: 'It designates a process agent in each state. It is required to activate your authority and we help coordinate it.' },
      { q: 'Do I need authority to be dispatched?', a: 'You need active operating authority or to lease onto a carrier. We help you go independent the right way.' },
      { q: 'Can you help if my authority was revoked?', a: 'Yes, we can guide you through the reinstatement process and compliance requirements.' },
    ],
    related: ['carrier-compliance', 'safety-dot-consulting', 'ifta-assistance'],
  },
  {
    slug: 'carrier-compliance',
    name: 'Carrier Compliance Services',
    shortName: 'Carrier Compliance',
    icon: 'ShieldCheck',
    category: 'support',
    seo: {
      title: 'Carrier Compliance Services | FMCSA & DOT Compliance Support',
      description:
        'Carrier compliance services to keep your trucking business audit-ready. We help with FMCSA compliance, driver qualification files, drug & alcohol programs, and DOT requirements.',
      keywords: ['carrier compliance', 'fmcsa compliance', 'dot compliance services', 'trucking compliance', 'carrier compliance support'],
    },
    hero: {
      headline: 'Stay Compliant, Stay on the Road',
      sub: 'Compliance failures park trucks and trigger fines. We help you stay audit-ready so you never lose revenue to paperwork.',
    },
    intro:
      'FMCSA and DOT compliance is non-negotiable, and falling behind can shut down your operation. We help you maintain driver qualification files, drug and alcohol program enrollment, MCS-150 updates, and the records you need to pass an audit with confidence.',
    benefits: [
      { title: 'Audit-Ready Recordkeeping', desc: 'Organized driver files and documentation to pass DOT audits.' },
      { title: 'Drug & Alcohol Program Help', desc: 'Guidance on consortium enrollment and Clearinghouse requirements.' },
      { title: 'MCS-150 & UCR Updates', desc: 'Stay current on biennial updates and annual registrations.' },
      { title: 'Ongoing Compliance Monitoring', desc: 'We help you track deadlines so nothing slips through the cracks.' },
    ],
    equipment: ['Driver Qualification Files', 'Drug & Alcohol Program', 'MCS-150 Updates', 'CSA Score Support'],
    industries: ['Owner Operators', 'Small Fleets', 'New Carriers', 'Growing Fleets'],
    faqs: [
      { q: 'What is required to stay FMCSA compliant?', a: 'Driver qualification files, drug & alcohol testing, current MCS-150, UCR, and accurate records, all of which we help maintain.' },
      { q: 'What is the FMCSA Clearinghouse?', a: 'A federal database for drug & alcohol violations. We help you meet query and reporting requirements.' },
      { q: 'How often do I update my MCS-150?', a: 'At least every two years, or sooner if your operation changes. We track the deadline for you.' },
      { q: 'Can you help improve my CSA scores?', a: 'Yes, we provide guidance on the behaviors and records that influence your CSA safety scores.' },
    ],
    related: ['safety-dot-consulting', 'mc-authority-assistance', 'ifta-assistance'],
  },
  {
    slug: 'rate-negotiation',
    name: 'Freight Rate Negotiation',
    shortName: 'Rate Negotiation',
    icon: 'TrendingUp',
    category: 'support',
    seo: {
      title: 'Freight Rate Negotiation Services | Get Paid More Per Mile',
      description:
        'Professional freight rate negotiation services. Our dispatchers negotiate higher rates with brokers using real market data so owner operators and fleets earn more per mile.',
      keywords: ['freight rate negotiation', 'rate negotiation dispatch', 'negotiate freight rates', 'higher truck rates', 'broker rate negotiation'],
    },
    hero: {
      headline: 'We Negotiate. You Earn More Per Mile.',
      sub: 'Brokers expect you to take the first offer. We don\'t. Our dispatchers use live market data to push every rate higher.',
    },
    intro:
      'The difference between an average dispatcher and a great one is negotiation. We back every conversation with current market data, lane history, and broker relationships to consistently move rates up, putting more money in your pocket on every load.',
    benefits: [
      { title: 'Data-Backed Negotiation', desc: 'Live market rates and lane history strengthen every counteroffer.' },
      { title: 'Accessorial Capture', desc: 'We negotiate detention, layover, TONU, and tarp pay you might otherwise lose.' },
      { title: 'Broker Relationships', desc: 'Established relationships help us secure better terms and repeat freight.' },
      { title: 'No Cheap Freight', desc: 'We protect your rate floor and walk away from loads that don\'t pay.' },
    ],
    equipment: ['Linehaul Rate Negotiation', 'Accessorial Pay', 'Detention & Layover', 'Fuel Surcharge'],
    industries: ['Owner Operators', 'Small Fleets', 'Dry Van', 'Reefer & Flatbed'],
    faqs: [
      { q: 'How do you negotiate higher rates?', a: 'We combine live market data, lane history, and broker relationships to justify and secure higher rates.' },
      { q: 'Do you set a minimum rate per mile?', a: 'Yes. We work to your rate floor and won\'t book freight below it without your approval.' },
      { q: 'Will you fight for detention and accessorials?', a: 'Always. Detention, layover, TONU, and tarp pay add up and we make sure they are captured.' },
      { q: 'Is rate negotiation included with dispatch?', a: 'Yes, aggressive rate negotiation is part of every load we book for you.' },
    ],
    related: ['dry-van-dispatch', 'broker-credit-checks', 'reefer-dispatch'],
  },
  {
    slug: 'broker-credit-checks',
    name: 'Broker Credit Checks',
    shortName: 'Broker Credit Checks',
    icon: 'Search',
    category: 'support',
    seo: {
      title: 'Broker Credit Checks | Get Paid On Every Load',
      description:
        'Broker credit check services that protect your revenue. We vet broker credit scores and payment history before booking so you only haul for brokers who pay on time.',
      keywords: ['broker credit checks', 'broker credit score', 'freight broker vetting', 'get paid trucking', 'broker payment history'],
    },
    hero: {
      headline: 'Never Haul for a Broker Who Won\'t Pay',
      sub: 'A great rate means nothing if you don\'t get paid. We vet broker credit and payment history before you ever roll.',
    },
    intro:
      'Non-payment and slow-pay brokers can sink a trucking business. Before we book any load, we check the broker\'s credit score, days-to-pay, and reputation so your hard miles always turn into money in the bank.',
    benefits: [
      { title: 'Pre-Booking Credit Vetting', desc: 'Every broker is screened for credit and payment history before you commit.' },
      { title: 'Days-to-Pay Awareness', desc: 'We factor payment speed into booking decisions to protect your cash flow.' },
      { title: 'Avoid Bad Actors', desc: 'We steer you away from brokers with poor reputations or slow-pay records.' },
      { title: 'Factoring-Friendly', desc: 'We help align loads with factoring company requirements when applicable.' },
    ],
    equipment: ['Credit Score Checks', 'Days-to-Pay Review', 'Payment History', 'Factoring Coordination'],
    industries: ['Owner Operators', 'Small Fleets', 'New Carriers', 'Factored Carriers'],
    faqs: [
      { q: 'Why are broker credit checks important?', a: 'They protect you from non-payment and slow-pay brokers so your loaded miles actually get paid.' },
      { q: 'Do you check credit on every load?', a: 'Yes. We screen broker credit and payment history before booking any load.' },
      { q: 'What if a broker has poor credit?', a: 'We avoid risky brokers or, if you choose to proceed, we make sure it aligns with your factoring terms.' },
      { q: 'Does this work with my factoring company?', a: 'Yes, we coordinate with factoring requirements to keep your advances flowing smoothly.' },
    ],
    related: ['rate-negotiation', 'dry-van-dispatch', 'carrier-compliance'],
  },
  {
    slug: 'ifta-assistance',
    name: 'IFTA Reporting Assistance',
    shortName: 'IFTA Assistance',
    icon: 'Calculator',
    category: 'support',
    seo: {
      title: 'IFTA Assistance & Reporting | Quarterly IFTA Filing Help',
      description:
        'IFTA assistance for owner operators and fleets. We help track miles and fuel by jurisdiction and prepare accurate quarterly IFTA reports so you file on time and avoid penalties.',
      keywords: ['ifta assistance', 'ifta reporting', 'quarterly ifta filing', 'ifta tax help', 'ifta fuel tax'],
    },
    hero: {
      headline: 'IFTA Reporting Without the Headache',
      sub: 'Quarterly fuel tax filing is tedious and error-prone. We help you track miles and fuel and file accurately, on time, every quarter.',
    },
    intro:
      'IFTA mistakes lead to audits and penalties. We help you organize jurisdiction miles and fuel purchases and prepare accurate quarterly IFTA reports, so you stay compliant and keep more of your money without the quarterly stress.',
    benefits: [
      { title: 'Mileage & Fuel Tracking', desc: 'Organized records of miles and fuel by jurisdiction.' },
      { title: 'Accurate Quarterly Prep', desc: 'We prepare your IFTA report so filings are correct and on time.' },
      { title: 'Audit Protection', desc: 'Clean, organized records that hold up if you are ever audited.' },
      { title: 'Penalty Avoidance', desc: 'Never miss a deadline or trigger penalties for inaccurate filings.' },
    ],
    equipment: ['Quarterly IFTA Prep', 'Mileage Tracking', 'Fuel Receipt Organization', 'Jurisdiction Reporting'],
    industries: ['Owner Operators', 'Small Fleets', 'Interstate Carriers', 'Multi-truck Operations'],
    faqs: [
      { q: 'When is IFTA due?', a: 'IFTA is filed quarterly. We track each deadline and prepare your report ahead of time.' },
      { q: 'What records do I need for IFTA?', a: 'Miles driven per jurisdiction and fuel purchases with receipts. We help you organize both.' },
      { q: 'What happens if I file IFTA wrong?', a: 'Errors can trigger audits and penalties. We prepare accurate reports to keep you protected.' },
      { q: 'Can you handle IFTA for multiple trucks?', a: 'Yes, we support single owner operators and multi-truck fleets.' },
    ],
    related: ['carrier-compliance', 'safety-dot-consulting', 'mc-authority-assistance'],
  },
  {
    slug: 'safety-dot-consulting',
    name: 'Safety & DOT Consulting',
    shortName: 'Safety & DOT Consulting',
    icon: 'HardHat',
    category: 'support',
    seo: {
      title: 'Safety & DOT Consulting | DOT Audit & CSA Score Help',
      description:
        'Safety and DOT consulting for carriers. We help you prepare for DOT audits, improve CSA scores, build safety programs, and stay compliant with FMCSA safety regulations.',
      keywords: ['safety dot consulting', 'dot audit help', 'csa score improvement', 'trucking safety program', 'dot compliance consultant'],
    },
    hero: {
      headline: 'Pass Your DOT Audit. Lower Your CSA Scores.',
      sub: 'Safety drives insurance costs and your ability to win freight. We build the programs and records that keep you safe and audit-ready.',
    },
    intro:
      'A strong safety program protects your authority, lowers insurance premiums, and improves your CSA scores. Our consultants help you prepare for DOT audits, build safety policies, and address violations before they become expensive problems.',
    benefits: [
      { title: 'DOT Audit Preparation', desc: 'Get your records and processes audit-ready before the auditor arrives.' },
      { title: 'CSA Score Improvement', desc: 'Targeted guidance to reduce violations and improve your safety scores.' },
      { title: 'Safety Program Setup', desc: 'Policies, training, and documentation that build a culture of compliance.' },
      { title: 'Violation Response', desc: 'Help responding to violations and DataQ challenges the right way.' },
    ],
    equipment: ['DOT Audit Prep', 'CSA Score Review', 'Safety Policy Setup', 'DataQ Support'],
    industries: ['Owner Operators', 'Small Fleets', 'Growing Fleets', 'New Carriers'],
    faqs: [
      { q: 'How do I prepare for a DOT audit?', a: 'We review your records, identify gaps, and get your documentation audit-ready before the review.' },
      { q: 'What affects my CSA scores?', a: 'Inspections, violations, and crash history across BASIC categories. We help you target the biggest drivers.' },
      { q: 'Can you help build a safety program?', a: 'Yes, we set up policies, training, and documentation tailored to your operation.' },
      { q: 'What is a DataQ challenge?', a: 'A formal request to correct inaccurate roadside inspection or crash data. We help you file them properly.' },
    ],
    related: ['carrier-compliance', 'mc-authority-assistance', 'ifta-assistance'],
  },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
export const getRelatedServices = (slugs = []) => slugs.map(getServiceBySlug).filter(Boolean);
export const EQUIPMENT_SERVICES = SERVICES.filter((s) => s.category === 'equipment');
export const SUPPORT_SERVICES = SERVICES.filter((s) => s.category === 'support');
